import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { categories, convert, formatResult, units } from '../utils/conversions';
import { parseNumber } from '../utils/numbers';

function UnitConverter() {
  const [category, setCategory] = useState('length');
  const [fromValue, setFromValue] = useState('');
  const [fromUnit, setFromUnit] = useState('m');
  const [toUnit, setToUnit] = useState('ft');

  const categoryUnits = units[category] || [];

  const { result, error } = useMemo(() => {
    const value = parseNumber(fromValue);
    if (value == null || !fromUnit || !toUnit) {
      return { result: null, error: '' };
    }
    try {
      const converted = convert(value, fromUnit, toUnit, category);
      return { result: formatResult(converted, toUnit), error: '' };
    } catch (err) {
      return { result: null, error: err.message };
    }
  }, [category, fromValue, fromUnit, toUnit]);

  const handleCategory = (event) => {
    const next = event.target.value;
    const nextUnits = units[next] || [];
    setCategory(next);
    setFromUnit(nextUnits[0]?.value || '');
    setToUnit(nextUnits[1]?.value || nextUnits[0]?.value || '');
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  };

  return (
    <ToolLayout title="Unit Converter">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Convert between common industrial units. The result updates as you type.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl>
                <InputLabel>Category</InputLabel>
                <Select value={category} label="Category" onChange={handleCategory}>
                  {categories.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <NumberField label="Value" value={fromValue} onChange={setFromValue} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl>
                <InputLabel>From</InputLabel>
                <Select value={fromUnit} label="From" onChange={(event) => setFromUnit(event.target.value)}>
                  {categoryUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title="Swap units">
                <IconButton onClick={swapUnits} color="primary" aria-label="Swap units">
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl>
                <InputLabel>To</InputLabel>
                <Select value={toUnit} label="To" onChange={(event) => setToUnit(event.target.value)}>
                  {categoryUnits.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result != null && (
            <ResultPanel items={[{ label: `Converted value (${toUnit})`, value: result, fullWidth: true }]} />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Length, area, volume, mass, force, pressure, energy, power, speed, torque, flow, and temperature all convert through SI base units, so every pairing in a category works.
      </Typography>
    </ToolLayout>
  );
}

export default UnitConverter;
